import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Key, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import { authApi } from '../../services/api';
import './ResetPassword.css';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [form, setForm] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        password: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
        }
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const togglePassword = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const validateForm = () => {
        if (!form.password || !form.confirmPassword) {
            setError('All fields are required');
            return false;
        }
        if (form.password.length < 8) {
            setError('Password must be at least 8 characters');
            return false;
        }
        if (form.password !== form.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        setError('');

        try {
            await authApi.resetPassword({
                token,
                password: form.password
            });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            if (err.response?.status === 400) {
                setTokenValid(false);
            } else {
                setError(err.response?.data?.error?.message || 'Failed to reset password');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!tokenValid) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-box">
                    <div className="reset-password-error">
                        <div className="error-icon">
                            <AlertCircle size={48} />
                        </div>
                        <h2>Invalid or Expired Link</h2>
                        <p>
                            This password reset link is invalid or has expired.
                            Please request a new one.
                        </p>
                        <Link to="/forgot-password">
                            <Button fullWidth>Request New Link</Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container">
            <div className="reset-password-box">
                <div className="reset-password-header">
                    <div className="reset-password-logo">
                        <div className="logo-icon">EP</div>
                    </div>
                    <h1>Reset Password</h1>
                    <p>Enter your new password</p>
                </div>

                {success ? (
                    <div className="reset-password-success">
                        <div className="success-icon">
                            <CheckCircle size={48} />
                        </div>
                        <h2>Password Reset Successful</h2>
                        <p>Your password has been reset. Redirecting to login...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="reset-password-form">
                        {error && (
                            <Alert type="error">{error}</Alert>
                        )}

                        <div className="password-field">
                            <Input
                                name="password"
                                type={showPasswords.password ? 'text' : 'password'}
                                label="New Password"
                                value={form.password}
                                onChange={handleChange}
                                icon={Key}
                                helperText="Minimum 8 characters"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => togglePassword('password')}
                            >
                                {showPasswords.password ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        <div className="password-field">
                            <Input
                                name="confirmPassword"
                                type={showPasswords.confirm ? 'text' : 'password'}
                                label="Confirm Password"
                                value={form.confirmPassword}
                                onChange={handleChange}
                                icon={Key}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => togglePassword('confirm')}
                            >
                                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        <Button type="submit" fullWidth loading={loading}>
                            Reset Password
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
